const t = (key, fallback = '') => (window.BL_I18N && BL_I18N[key]) || fallback || key;
const CURRENT_LOCALE = (window.BL_I18N && BL_I18N.locale) || document.documentElement.lang || 'en-US';

const IS_DE = String(CURRENT_LOCALE).toLowerCase().startsWith('de');

class TopPicksSection {
    constructor(opts = {}) {
        this.proxyUrl   = opts.proxyUrl || '/wp-content/themes/basellodging-theme/api-proxi.php';
        this.gridSel    = opts.gridSelector || '#our-top-picks';
        this.limit      = opts.limit || 12;

        this.lang = (
            (window.ICL_LANGUAGE_CODE) ||
            (window.BL_AJAX && BL_AJAX.lang) ||
            (window.BL_I18N && BL_I18N.locale) ||
            document.documentElement.lang || 'en'
        ).toString().toLowerCase().split(/[-_]/)[0];

        this.unitMetaMap = new Map(); // unitId -> {title, permalink}
    }

    async boot() {
        const $grid = $(this.gridSel);
        if (!$grid.length) return;

        const dataset = $grid.data();
        const preferredRate = String(dataset.preferredRate || '').toUpperCase(); // DAILY | MONTHLY | ''

        const unitsCsv = String(dataset.units || '').trim();
        const ids = unitsCsv ? unitsCsv.split(',').map(s => s.trim()).filter(Boolean) : [];
        if (!ids.length) return;

        try {
            const [units] = await Promise.all([
                this._fetchUnitsByIds(ids),
                this._fetchTitlesByUnitIds(ids)
            ]);
            if (!Array.isArray(units) || !units.length) return;

            // сортуємо як у data-units і відкидаємо без WP-поста
            const keyOf = (u) => String(u.externalId ?? u.id ?? u.unitId ?? '');
            const byId = new Map(units.map(u => [keyOf(u), u]));
            const ordered = ids
                .map(id => {
                    const u = byId.get(String(id));
                    const meta = this.unitMetaMap.get(String(id));
                    return (u && meta && meta.permalink) ? u : null;
                })
                .filter(Boolean)
                .slice(0, this.limit);
            if (!ordered.length) return;

            this.preferredRate = preferredRate; // лише для відображення ціни
            const html = ordered.map(u => this._card(u)).join('');
            $grid.html(html);
        } catch(e) {
            console.warn('[TopPicks] boot failed:', e);
        }
    }

    async _fetchTitlesByUnitIds(ids = []) {
        const ajaxUrl = (window.BL_AJAX && BL_AJAX.url) || '/wp-admin/admin-ajax.php';
        const nonce   = (window.BL_AJAX && BL_AJAX.nonce) || '';
        try {
            const resp = await $.ajax({
                url: ajaxUrl, method: 'POST', dataType: 'json',
                data: { action: 'bl_get_unit_titles', _ajax_nonce: nonce, 'unitIds[]': ids, lang: this.lang }
            });
            const items = resp?.data?.items || [];
            items.forEach(it => {
                const k = String(it?.unitId ?? '');
                if (!k) return;
                this.unitMetaMap.set(k, {
                    title: (it?.title || '').trim() || null,
                    permalink: (it?.permalink || '').trim() || null
                });
            });
        } catch(e) {
            console.warn('[TopPicks] titles fetch failed', e);
        }
    }

    async _fetchUnitsByIds(ids) {
        const payload = { ids: ids.join(','), limit: this.limit, page: 1 };
        try {
            const resp = await $.ajax({
                url: `${this.proxyUrl}?type=getApartments`,
                method: 'POST', contentType: 'application/json',
                dataType: 'json', data: JSON.stringify(payload)
            });
            const data = resp?.data ?? resp ?? [];
            return Array.isArray(data) ? data : [];
        } catch(e) {
            console.error('[TopPicks] fetch error', e);
            return [];
        }
    }

    _esc(s){return String(s??'').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;').replace(/'/g,'&#039;');}
    _nf(n){return new Intl.NumberFormat('en-CH',{maximumFractionDigits:2}).format(Number(n)||0);}

    _priceLabel(unit){
        const forceDaily = String(this.preferredRate || '').toUpperCase() === 'DAILY';

        let pricingToUse;
        if (Array.isArray(unit?.pricing)) {
            pricingToUse = forceDaily ? (unit.pricing[1] || unit.pricing[0]) : unit.pricing[0];
        } else {
            pricingToUse = unit?.pricing;
        }

        if (!pricingToUse) return '';
        const amount  = pricingToUse.amount ?? pricingToUse.pricePerNight ?? 0;
        const unitTxt = String(pricingToUse.rateType).toUpperCase() === 'MONTHLY' ? `/ ${t('month','month')}` : `/ ${t('night','night')}`;
        return `CHF ${this._nf(amount)} <span class="text-xs text-gray-500">${unitTxt}</span>`;
    }

    _resolveImage(unit){
        const base='/wp-content/themes/basellodging-theme/imgs';
        const placeholder = `${base}/placeholder.svg`;
        return (unit?.mainImage?.url) ? String(unit.mainImage.url).trim() : placeholder;
    }

    _card(unit){
        const key  = String(unit.externalId ?? unit.id ?? unit.unitId ?? '');
        const meta = key ? this.unitMetaMap.get(key) : null;

        const title =
            (meta?.title && String(meta.title).trim())
            || unit.unitName
            || unit.unitTypeName
            || unit.referenceNumber
            || t('apartment','Apartment');

        const detailUrl = (meta?.permalink && String(meta.permalink).trim()) ? meta.permalink : '#';

        const loc = [unit?.addressLine1, unit?.areaName, unit?.city]
            .map(v => (v ?? '').toString().trim())
            .filter(v => v.length > 0)
            .join(', ');
        const img = this._resolveImage(unit);
        const bedrooms = unit?.specifications?.numberOfBedrooms;
        const bedroomsLabel = (bedrooms === 0 || bedrooms === null) ? t('studio','Studio') : (bedrooms ?? '');
        const guests = unit?.specifications?.maxOccupancy;
        const areaMin = unit?.specifications?.area?.min;
        const areaMax = unit?.specifications?.area?.max;
        const areaLabel = (areaMin && areaMax && areaMin !== areaMax) ? `${areaMin}–${areaMax}m²`
            : (areaMin || areaMax) ? `${areaMin || areaMax}m²` : null;
        const features = Array.isArray(unit.features) ? unit.features.slice(0,3) : [];
        const priceHtml = this._priceLabel(unit);

        return `
<a href="${this._esc(detailUrl)}" class="rounded-lg bg-card text-card-foreground overflow-hidden border-0 shadow-md h-full flex flex-col apartment-item">
  <div class="relative">
    <img src="${this._esc(img)}" alt="${this._esc(title)}" class="w-full h-48 sm:h-52 md:h-48 object-cover" loading="lazy">
  </div>
  <div class="p-4 sm:p-5 md:p-4 flex flex-col flex-1">
    <div class="mb-2">
      <div class="flex items-center text-xs sm:text-sm text-gray-600">
        <svg xmlns="http://www.w3.org/2000/svg" class="lucide lucide-map-pin w-3 h-3 sm:w-4 sm:h-4 mr-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0"></path><circle cx="12" cy="10" r="3"></circle></svg>
        <span class="truncate">${this._esc(loc)}</span>
      </div>
    </div>
    <h3 class="font-semibold text-sm sm:text-base text-basel-gray mb-2 line-clamp-2 leading-tight">${this._esc(title)}</h3>

    <div class="flex items-center gap-x-4 mb-3 text-sm text-gray-600">
      ${guests != null ? `
        <div class="flex items-center space-x-1">
          <svg xmlns="http://www.w3.org/2000/svg" class="lucide lucide-users w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M22 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>
          <span>${this._esc(guests)}</span>
        </div>` : ``}
      ${bedroomsLabel !== '' ? `
        <div class="flex items-center space-x-1">
          <svg xmlns="http://www.w3.org/2000/svg" class="lucide lucide-bed w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M2 4v16"></path><path d="M2 8h18a2 2 0 0 1 2 2v10"></path><path d="M2 17h20"></path><path d="M6 8v9"></path></svg>
          <span>${this._esc(bedroomsLabel)}</span>
        </div>` : ``}
      ${areaLabel ? `
        <div class="flex items-center space-x-1">
          <svg xmlns="http://www.w3.org/2000/svg" class="lucide lucide-square w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect width="18" height="18" x="3" y="3" rx="2"></rect></svg>
          <span>${this._esc(areaLabel)}</span>
        </div>` : ``}
    </div>

    <div class="flex items-center space-x-2 mb-3 flex-wrap gap-y-1">
      ${features.map(a => `
        <div class="flex items-center space-x-1 text-xs text-gray-600">
          ${a.iconClass? `<i class="${a.iconClass}"></i>` : ""}
          <span class="hidden sm:inline">${IS_DE ? (this._esc(a.nameLang2) || "") : (this._esc(a.name) || "")}</span>
        </div>
      `).join('')}
    </div>

    <div class="flex items-center justify-between pt-2 border-t border-gray-100 mt-auto">
      <div class="flex items-baseline space-x-1">
        <span class="text-base sm:text-lg font-bold text-basel-gray">${this._priceLabel(unit)}</span>
      </div>
        <button class="inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium rounded-md bg-basel-blue text-white hover:bg-basel-blue/90 text-xs sm:text-sm px-3 sm:px-4 h-8 sm:h-9">
        <span class="hidden sm:inline">${t('view_details','View Details')}</span><span class="sm:hidden">${t('view','View')}</span>
        </button>
    </div>
  </div>
</a>`;
    }
}

$(document).ready(() => {
    new TopPicksSection({
        proxyUrl: '/wp-content/themes/basellodging-theme/api-proxi.php',
        gridSelector: '#our-top-picks',
        limit: 12
    }).boot();
});